/**
 * The study's own products, and the limits its protocol sets.
 *
 * A focused protocol-reading step beside the main extraction (Jim, 2026-10-27,
 * batch 3c). The main extraction lists every product a protocol names; this
 * step records each one's ROLE (the study's investigational product, a
 * comparator, a background treatment, or a product only mentioned, such as the
 * device an earlier study used) and each LIMIT the protocol sets, kept apart
 * from ranges observed in earlier studies.
 *
 * Nothing is taken on the model's word: every item carries an exact quote, and
 * `groundStudyProducts` keeps it only when the quote is in the protocol's own
 * text, names the product (or contains the limit's number). Platform-wide: the
 * prompt's examples are invented, never a real study's products.
 */

export type ProductRole = "investigational" | "comparator" | "background" | "mentioned_only";
const ROLES: ReadonlySet<string> = new Set(["investigational", "comparator", "background", "mentioned_only"]);

export interface StudyProduct {
  name: string;
  role: ProductRole;
  /** The protocol sentence the role was read from. */
  quote: string;
}

export interface ProtocolLimit {
  /** What is limited: "daily dose", "amplitude". */
  parameter: string;
  maximum: number;
  unit: string;
  quote: string;
}

export interface StudyProductsFacts {
  products: StudyProduct[];
  limits: ProtocolLimit[];
  /** Items the model returned that the protocol text did not support. */
  dropped: Array<{ item: string; reason: string }>;
}

type Rec = Record<string, unknown>;
const obj = (v: unknown): Rec => (v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : {});
const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** Lower case, trademark signs removed, every run of whitespace one space. */
function norm(s: string): string {
  return s
    .replace(/[™®©]/g, "")
    .replace(/[‐-―]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * The form two strings are compared in: `norm`, then every space and hyphen
 * removed. Text read from a PDF loses superscripts and splits a radiolabel
 * ("211 At-MABG", "123 I MIBG") where the model writes "211At-MABG"; measured on
 * an oncology protocol 2026-10-27, where the study's own drug failed the check
 * in 5 of 5 runs. Letters, digits and their order still have to match.
 */
const key = (s: string) => norm(s).replace(/[\s\-‐-―]/g, "");

/**
 * Keep only what the protocol's text supports. With no readable text nothing
 * can be checked, so nothing is kept: an unverified role is worse than none.
 */
export function groundStudyProducts(raw: unknown, protocolText: string): StudyProductsFacts {
  const text = key(protocolText || "");
  const r = obj(raw);
  const out: StudyProductsFacts = { products: [], limits: [], dropped: [] };
  const drop = (item: string, reason: string) => out.dropped.push({ item, reason });

  for (const p of Array.isArray(r.products) ? r.products : []) {
    const name = str(obj(p).name);
    const role = str(obj(p).role);
    const quote = str(obj(p).quote);
    if (!name || !ROLES.has(role) || !quote) {
      drop(name || "(unnamed product)", "incomplete");
      continue;
    }
    if (!text || !text.includes(key(quote))) {
      drop(name, "quote not in the protocol");
      continue;
    }
    if (!key(quote).includes(key(name))) {
      drop(name, "the quote does not name the product");
      continue;
    }
    out.products.push({ name, role: role as ProductRole, quote });
  }

  for (const l of Array.isArray(r.limits) ? r.limits : []) {
    const parameter = str(obj(l).parameter);
    const unit = str(obj(l).unit);
    const quote = str(obj(l).quote);
    const maximum = obj(l).maximum;
    if (!parameter || typeof maximum !== "number" || !Number.isFinite(maximum) || !quote) {
      drop(parameter || "(unnamed limit)", "incomplete");
      continue;
    }
    if (!text || !text.includes(key(quote))) {
      drop(parameter, "quote not in the protocol");
      continue;
    }
    const numbers = (norm(quote).match(/\d+(?:\.\d+)?/g) ?? []).map(Number);
    if (!numbers.includes(maximum)) {
      drop(parameter, "the quote does not contain the maximum");
      continue;
    }
    out.limits.push({ parameter, maximum, unit, quote });
  }
  return out;
}

/**
 * Words that say what KIND of thing a product is, not which one: two names
 * sharing only these are not the same product.
 */
const GENERIC = new Set([
  "a", "an", "and", "the", "of", "with", "for", "in", "on", "or",
  "system", "systems", "device", "devices", "kit", "kits", "model",
  "tablet", "tablets", "capsule", "capsules", "injection", "solution", "oral",
  "mg", "mcg", "ml", "dose", "therapy", "treatment", "product",
]);

/** The distinctive words of a name: trademark signs, punctuation and case removed. */
function distinctWords(s: string): Set<string> {
  const w = norm(s).replace(/[^\p{L}\p{N} ]/gu, " ").split(/\s+/).filter((x) => x && !GENERIC.has(x));
  return new Set(w);
}

/**
 * How closely an intervention names a product: the distinctive words they
 * share, and the share of the product's words that covers. Null when it is not
 * a match: a match needs half the product's words, and a single shared word
 * only when that is the whole product name ("Medtronic" alone does not make
 * "Medtronic Activa" the study's "Medtronic Percept"). The two readings word a
 * device differently, so one name need not contain the other (measured
 * 2026-10-27: "Vercise directional DBS system" against "Vercise DBS System").
 */
function closeness(intervention: Set<string>, product: string): { shared: number; coverage: number } | null {
  const pw = distinctWords(product);
  if (!pw.size) return null;
  let shared = 0;
  for (const w of pw) if (intervention.has(w)) shared++;
  const coverage = shared / pw.size;
  if (!shared || coverage < 0.5 || (shared === 1 && coverage < 1)) return null;
  return { shared, coverage };
}

/**
 * The study's own interventions: the analysis's list minus any the protocol
 * only MENTIONS. Each intervention is matched to the product it names most
 * closely (most shared words, then the larger share of that product's name);
 * it is left out when that product is mentioned only and no product the study
 * uses matches it as closely. An analysis made before this step existed keeps
 * every intervention, unchanged.
 */
export function studyInterventions<T = Rec>(analysis: { interventions?: unknown; studyProducts?: unknown } | null | undefined): T[] {
  const list = Array.isArray(analysis?.interventions) ? (analysis!.interventions as T[]) : [];
  const facts = obj(analysis?.studyProducts);
  const products = Array.isArray(facts.products) ? (facts.products as StudyProduct[]) : [];
  if (!products.length) return list;
  return list.filter((iv) => {
    const name = typeof iv === "string" ? iv.trim() : str(obj(iv).name);
    if (!name) return true;
    const words = distinctWords(name);
    let best: { shared: number; coverage: number; mentioned: boolean; used: boolean } | null = null;
    for (const p of products) {
      const c = closeness(words, str(p?.name));
      if (!c) continue;
      const mentioned = p.role === "mentioned_only";
      if (!best || c.shared > best.shared || (c.shared === best.shared && c.coverage > best.coverage)) {
        best = { ...c, mentioned, used: !mentioned };
      } else if (c.shared === best.shared && c.coverage === best.coverage) {
        if (mentioned) best.mentioned = true;
        else best.used = true;
      }
    }
    return !best || !best.mentioned || best.used;
  });
}

/** The prompt for the step. Examples are INVENTED (platform-wide, never a real study's products). */
export function buildStudyProductsPrompt(): string {
  return [
    "You are reading a clinical study protocol. Report two things, as JSON, quoting the protocol exactly.",
    "",
    "1. PRODUCTS: every drug, biologic, device, procedure-delivered product or placebo the protocol names, each with its ROLE in THIS study. Do not list formulation ingredients, excipients or diluents (solvents, saline, water for injection) as products.",
    '   - "investigational": what this study tests (the study drug, study device, study procedure).',
    '   - "comparator": what it is compared against (placebo, sham, active control).',
    '   - "background": a treatment participants receive or continue that the study does not test (standard of care, rescue medication, premedication).',
    '   - "mentioned_only": named but not given in this study (used in an earlier or pilot study, in background literature, as an alternative, or as an excluded medication).',
    "   Decide the role from what the protocol says happens in THIS study. A product named in a list of study devices or study drugs is not mentioned_only.",
    "",
    "2. LIMITS: every maximum the protocol sets for what a participant receives in this study (a maximum dose or exposure, a maximum device setting, a cap that must not be exceeded), with its number and unit. Not preparation volumes, formulation amounts, visit windows or scheduling tolerances.",
    "   Do NOT report ranges OBSERVED in earlier studies, typical ranges, or starting values as limits. Where a table shows an observed range beside a maximum allowed value, report only the maximum allowed value.",
    "",
    "For every item give `quote`: an EXACT sentence or table row copied from the protocol that supports it (the product's name, or the limit's number, must appear in it). Copy it exactly; do not paraphrase.",
    "",
    "Invented example (not from this protocol):",
    '  "Participants receive Zentrovant 40 mg tablets once daily or matching placebo tablets." -> Zentrovant investigational; matching placebo comparator.',
    '  "An earlier pilot study used the AcmeStim Model 7 system." -> AcmeStim Model 7 mentioned_only.',
    '  "Table 4 | Amplitude (mA) | observed 0.5 - 2.0 | maximum allowable 4" -> limit: Amplitude, maximum 4, unit mA (NOT 0.5 - 2.0).',
    "",
    'Return ONLY: {"products":[{"name":"","role":"","quote":""}],"limits":[{"parameter":"","maximum":0,"unit":"","quote":""}]}',
    "Use empty arrays when the protocol names no product or sets no limit.",
  ].join("\n");
}

/**
 * Run `attempt` until it returns at least one product, or `maxAttempts` is
 * reached (Gemini 3 sometimes answers empty without reading; see
 * agents/scheduleExtractionPolicy.ts). A protocol with no product (a
 * behavioural study) legitimately ends empty after the last attempt.
 */
export async function readStudyProductsWithRetry(
  attempt: (n: number) => Promise<unknown>,
  maxAttempts: number,
  onAttempt?: (n: number, productCount: number) => void,
): Promise<{ raw: unknown; attempts: number }> {
  const max = Math.max(1, Math.floor(maxAttempts));
  let raw: unknown = null;
  for (let n = 1; n <= max; n++) {
    raw = await attempt(n);
    const count = Array.isArray(obj(raw).products) ? (obj(raw).products as unknown[]).length : 0;
    onAttempt?.(n, count);
    if (count > 0) return { raw, attempts: n };
  }
  return { raw, attempts: max };
}
